/**
 * API Key Card Component
 * 显示特定资产类型的 API key 配置
 */

import { useState, useMemo, useEffect } from 'react';
import { Card } from './Card';
import { Input } from './Input';
import { Button } from './Button';
import { useTranslation } from 'react-i18next';
import { useSettingsStore } from '@/stores/settingsStore';
import { providerRegistry } from '@/services/providerRegistry';
import { AssetType } from '@/types/api';

interface ApiKeyItem {
  provider: 'twelvedata' | 'coinmarketcap' | 'alphavantage' | 'finnhub' | 'marketstack' | 'coingecko' | 'cryptowatchr' | 'financialmodelingprep';
  name: string;
  priority: number;
  requiresKey: boolean;
}

interface ApiKeyCardProps {
  assetType: 'crypto' | 'stock' | 'index' | 'forex' | 'bond';
  providers: ApiKeyItem[];
}

export const ApiKeyCard = ({ assetType, providers }: ApiKeyCardProps) => {
  const { t } = useTranslation();
  const { apiKeys, setApiKey } = useSettingsStore();
  
  // 防护：确保 providers 和 apiKeys 存在
  if (!providers || !Array.isArray(providers)) {
    console.warn('ApiKeyCard: providers is not an array', providers);
    return null;
  }
  
  if (!apiKeys || typeof apiKeys !== 'object') {
    console.warn('ApiKeyCard: apiKeys is not an object', apiKeys);
    return null;
  }
  
  // 按优先级排序
  const sortedProviders = [...providers].sort((a, b) => a.priority - b.priority);
  
  const [testingProvider, setTestingProvider] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, { valid: boolean; message: string } | null>>({});
  // 本地状态管理输入框的值
  const [inputValues, setInputValues] = useState<Record<string, string | undefined>>({});
  
  // 当 apiKeys 变化时，同步更新输入框的值（仅在输入框没有本地状态时）
  useEffect(() => {
    if (!apiKeys || !sortedProviders) return;
    
    const newInputValues: Record<string, string> = {};
    sortedProviders.forEach(item => {
      if (item.requiresKey && apiKeys) {
        const providerKey = item.provider as keyof typeof apiKeys;
        const storedKey = (providerKey in apiKeys && apiKeys[providerKey]) ? (apiKeys[providerKey] || '') : '';
        // 只有当输入框没有本地状态时才更新
        if (inputValues[item.provider] === undefined) {
          newInputValues[item.provider] = storedKey;
        }
      }
    });
    if (Object.keys(newInputValues).length > 0) {
      setInputValues(prev => ({ ...prev, ...newInputValues }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKeys, sortedProviders]);
  
  const handleTestApiKey = async (provider: string) => {
    setTestingProvider(provider);
    setTestResults(prev => {
      const newResults = { ...prev };
      delete newResults[provider];
      return newResults;
    });
    
    try {
      const apiProvider = providerRegistry.getProvider(provider);
      if (!apiProvider) {
        setTestResults(prev => ({
          ...prev,
          [provider]: { valid: false, message: 'Provider not found' }
        }));
        return;
      }
      
      const isValid = await apiProvider.testConnection({ timeout: 5000 });
      setTestResults(prev => ({
        ...prev,
        [provider]: {
          valid: isValid,
          message: isValid ? t('settings.apiKeys.configured') : 'Test failed'
        }
      }));
    } catch (error: any) {
      setTestResults(prev => ({
        ...prev,
        [provider]: {
          valid: false,
          message: error.message || 'Test failed'
        }
      }));
    } finally {
      setTestingProvider(null);
    }
  };
  
  const handleSaveApiKey = (provider: string, key: string | null) => {
    // 检查是否是有效的 provider key
    if (provider === 'coingecko' || provider === 'cryptowatchr') {
      // 这些 provider 不需要 key，不需要保存
      return;
    }
    if (apiKeys && provider in apiKeys) {
      setApiKey(provider as keyof typeof apiKeys, key);
      setTestResults(prev => ({
        ...prev,
        [provider]: {
          valid: true,
          message: key ? t('settings.apiKeys.configured') : t('settings.apiKeys.notConfigured')
        }
      }));
      // 保存后清除本地输入状态，使用存储的值
      setInputValues(prev => {
        const newValues = { ...prev };
        delete newValues[provider];
        return newValues;
      });
    }
  };
  
  const handleClearApiKey = (provider: string) => {
    if (provider === 'coingecko' || provider === 'cryptowatchr') {
      return;
    }
    if (apiKeys && provider in apiKeys) {
      setApiKey(provider as keyof typeof apiKeys, null);
      setTestResults(prev => {
        const newResults = { ...prev };
        delete newResults[provider];
        return newResults;
      });
    }
  };
  
  const getTitle = () => {
    const titles: Record<string, string> = {
      crypto: t('settings.apiKeys.crypto.title'),
      stock: t('settings.apiKeys.stock.title'),
      index: t('settings.apiKeys.index.title'),
      forex: t('settings.apiKeys.forex.title'),
      bond: t('settings.apiKeys.bond.title') || 'Bond',
    };
    return titles[assetType] || assetType;
  };
  
  const getDescription = () => {
    // Description removed as per user request
    return '';
  };
  
  return (
    <Card>
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            {getTitle()}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {getDescription()}
          </p>
        </div>
        
        <div className="space-y-3">
          {sortedProviders && sortedProviders.length > 0 ? (
            sortedProviders.map((item) => {
            // 处理不需要 key 的 provider
            const providerKey = item.provider as keyof typeof apiKeys;
            const storedKey = (apiKeys && providerKey in apiKeys && item.requiresKey) 
              ? (apiKeys[providerKey] || '') 
              : '';
            // 使用本地状态的值，如果没有则使用存储的值
            const currentKey = (inputValues[item.provider] !== undefined ? inputValues[item.provider] : storedKey) || '';
            const testResult = testResults[item.provider];
            const isTesting = testingProvider === item.provider;
            
            return (
              <div key={item.provider} className="border-b border-gray-200 dark:border-gray-700 pb-3 last:border-0 last:pb-0">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {item.priority}. {t(`settings.apiKeys.provider.${item.provider}`)}
                    </span>
                    {item.requiresKey ? (
                      <span className="text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 rounded">
                        {t('settings.apiKeys.keyRequired')}
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded">
                        {t('settings.apiKeys.noKeyRequired')}
                      </span>
                    )}
                  </div>
                </div>
                
                {item.requiresKey && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        type="password"
                        data-provider={item.provider}
                        value={currentKey}
                        onChange={(e) => {
                          // 实时更新本地状态
                          setInputValues(prev => ({
                            ...prev,
                            [item.provider]: e.target.value
                          }));
                        }}
                        onBlur={(e) => {
                          const newKey = e.target.value;
                          if (newKey !== storedKey) {
                            handleSaveApiKey(item.provider, newKey || null);
                            // 保存后同步本地状态
                            setInputValues(prev => ({
                              ...prev,
                              [item.provider]: newKey
                            }));
                          }
                        }}
                        placeholder={storedKey ? t('settings.apiKeys.configured') : t('settings.apiKeys.notConfigured')}
                        className="flex-1"
                      />
                      <Button
                        onClick={() => handleTestApiKey(item.provider)}
                        disabled={isTesting || !currentKey}
                        variant="secondary"
                        size="sm"
                      >
                        {isTesting ? t('settings.apiKeys.testing') : t('settings.apiKeys.test')}
                      </Button>
                      <Button
                        onClick={() => {
                          const value = inputValues[item.provider] !== undefined ? inputValues[item.provider] : currentKey;
                          if (item.requiresKey) {
                            handleSaveApiKey(item.provider, value || null);
                            // 保存后同步本地状态
                            setInputValues(prev => {
                              const newValues = { ...prev };
                              if (value) {
                                newValues[item.provider] = value;
                              } else {
                                delete newValues[item.provider];
                              }
                              return newValues;
                            });
                          }
                        }}
                        variant="primary"
                        size="sm"
                      >
                        {t('settings.apiKeys.save')}
                      </Button>
                      {currentKey && item.requiresKey && (
                        <Button
                          onClick={() => handleClearApiKey(item.provider)}
                          variant="ghost"
                          size="sm"
                        >
                          {t('settings.apiKeys.clear')}
                        </Button>
                      )}
                    </div>
                    {testResult && (
                      <div
                        className={`text-xs ${
                          testResult.valid
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-red-600 dark:text-red-400'
                        }`}
                      >
                        {testResult.message}
                      </div>
                    )}
                  </div>
                )}
                
                {!item.requiresKey && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {t('settings.apiKeys.noKeyRequired')} - {t('settings.apiKeys.configured')}
                  </div>
                )}
              </div>
            );
            })
          ) : (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {t('settings.apiKeys.notConfigured')}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
